import { getTokenClaims } from '@api/services/sessionService'
import { resolveCbUserId } from '@/analytics/identity'

/**
 * CB's built-in administrator account ("CB Admin", MesaTypesBase.SysAdminId) — spec 173.
 *
 * The same id on every tenant (centerbase Endpoint/MesaUtilitiesBase/MesaTypesBase.cs:15);
 * CB's own test for it is a plain identity comparison (ServerHelper.IsSysAdmin). On
 * vladdemo it is the e2e "CBS" account. Pinned to cb-api's constant by
 * `src/utils/__tests__/sysAdmin.test.js`, so the two cannot drift.
 *
 * ⚠️ This is a DISPLAY hint, not a permission. It decides only whether the saved-views
 * menu offers the "Other users’ views" section. cb-api decides whether anything is read
 * (`cb-api/src/domains/reports/sysAdminGate.js`: the verified token's id, then CB itself),
 * and a refusal there is shown as one.
 */
export const CB_SYSADMIN_USER_ID = 'a3f13d51-9f5a-4088-bf5e-f701cd31bc59'

/** cb-api's refusal code for "you are not the SysAdmin" (sysAdminGate.js NOT_SYSADMIN_CODE). */
export const NOT_SYSADMIN_CODE = 'not-sysadmin'

/** True only for the exact id, in any case (CB returns ids in inconsistent case). */
export const isSysAdminUserId = (id) => typeof id === 'string' && id.toLowerCase() === CB_SYSADMIN_USER_ID

/**
 * Is the signed-in token CB's SysAdmin? Resolves the user id exactly as cb-api's
 * `parseAuth` does (the `cbuserid` claim, else the id in `sub`) via the one shared
 * mirror of it, `resolveCbUserId`. Never throws: no token getter (the IQ embed, a test),
 * no token, or an undecodable token all answer false — the section stays hidden.
 */
export async function signedInAsSysAdmin() {
  try {
    return isSysAdminUserId(resolveCbUserId(await getTokenClaims()))
  } catch {
    return false
  }
}

/**
 * Read a failed `include=others` request into what the section shows.
 *
 * `retryable` is false only for cb-api's coded "not the SysAdmin" refusal: asking again
 * cannot change who you are. Everything else (CB could not confirm, storage unreadable,
 * the server unreachable, a reply we could not read) is worth another try, and none of
 * it may ever render as "nobody else has saved a view".
 *
 * @returns {{ message: string, retryable: boolean }}
 */
export function othersReadFailure(err) {
  const res = err?.response
  if (res) {
    const sentence = typeof res.data?.error === 'string' && res.data.error.trim() ? res.data.error : null
    return {
      message: sentence || `The server answered ${res.status}.`,
      retryable: !(res.status === 403 && res.data?.code === NOT_SYSADMIN_CODE),
    }
  }
  if (err?.request || err?.isAxiosError) return { message: 'The server could not be reached.', retryable: true }
  return { message: typeof err?.message === 'string' ? err.message : '', retryable: true }
}
